import React, { useMemo, useEffect } from "react";
import { Select, SelectProps } from "@mantine/core";

import { RoomType } from "@/utils/api";
import { useRoomTypesContext } from "@/contexts";
import { LoadingIndicator } from "@components/generic";

export interface RoomTypeSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange"> {
  /** Currently selected room type ID */
  value?: string | null | undefined;

  /** Callback fired when room type selection changes */
  onChange?: (roomTypeId: string | null, roomType?: RoomType) => void;

  /** Placeholder text when no room type is selected */
  placeholder?: string;

  /** Whether to include inactive room types in the list */
  includeInactive?: boolean;

  /** Custom filter function to filter room types */
  roomTypeFilter?: (roomType: RoomType) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Placeholder text shown while loading */
  loadingPlaceholder?: string;

  /** Text shown when no room types are available */
  emptyText?: string;

  /** Error message to display */
  error?: string | number | null | undefined;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size of the selector */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const RoomTypeSelector: React.FC<RoomTypeSelectorProps> = ({
  value,
  onChange,
  placeholder = "Select a room type",
  includeInactive = false,
  roomTypeFilter,
  required = false,
  loadingPlaceholder = "Loading room types...",
  emptyText = "No room types available",
  error,
  disabled,
  size = "md",
  ...selectProps
}) => {
  const { roomTypes, loading, isEmpty, lastError } = useRoomTypesContext();

  // Filter and prepare room type options
  const roomTypeOptions = useMemo(() => {
    let filteredRoomTypes = roomTypes;

    // Apply status filter (status 100 = active, 0 = disabled)
    if (!includeInactive) {
      filteredRoomTypes = filteredRoomTypes.filter(
        (roomType) => roomType.status === 100 // Only show active room types
      );
    }

    // Apply custom filter if provided
    if (roomTypeFilter) {
      filteredRoomTypes = filteredRoomTypes.filter(roomTypeFilter);
    }

    // Sort by orderNo (ascending) to maintain proper order
    filteredRoomTypes.sort((a, b) => {
      const orderA = a.orderNo ?? 999;
      const orderB = b.orderNo ?? 999;
      return orderA - orderB;
    });

    // Convert to select options
    return filteredRoomTypes.map((roomType) => ({
      value: roomType.id || "",
      label: roomType.name || "Unnamed Room Type",
      disabled: roomType.status === 0, // Disabled room types have status 0
    }));
  }, [roomTypes, includeInactive, roomTypeFilter]);

  // Create a map for quick room type lookup by ID
  const roomTypeMap = useMemo(() => {
    const map = new Map();
    roomTypes.forEach((roomType) => {
      if (roomType.id) {
        map.set(roomType.id, roomType);
      }
    });
    return map;
  }, [roomTypes]);

  // Auto-select first room type if required and no value is set
  useEffect(() => {
    if (
      required &&
      !value &&
      roomTypeOptions.length > 0 &&
      !loading &&
      onChange
    ) {
      const firstRoomType = roomTypeOptions[0];
      if (firstRoomType && !firstRoomType.disabled) {
        const selectedRoomType = roomTypeMap.get(firstRoomType.value);
        onChange(firstRoomType.value, selectedRoomType);
      }
    }
  }, [required, value, roomTypeOptions, loading, onChange, roomTypeMap]);

  // Handle onChange with room type object lookup
  const handleChange = (selectedRoomTypeId: string | null) => {
    if (selectedRoomTypeId) {
      const selectedRoomType = roomTypeMap.get(selectedRoomTypeId);
      onChange?.(selectedRoomTypeId, selectedRoomType);
    } else {
      // When clearing selection, pass null for both ID and room type
      onChange?.(null, undefined);
    }
  };

  // Determine the current state and configure select props accordingly
  const getSelectState = () => {
    // Loading state
    if (loading && roomTypes.length === 0) {
      return {
        data: [],
        placeholder: loadingPlaceholder,
        disabled: true,
        rightSection: <LoadingIndicator size={size} color="gray" />,
      };
    }

    // Error state
    if (lastError && !loading) {
      return {
        data: [],
        placeholder: "Error loading room types",
        disabled: true,
        error: error || "Failed to load room types",
      };
    }

    // Empty state
    if (isEmpty && !loading) {
      return {
        data: [],
        placeholder: emptyText,
        disabled: true,
      };
    }

    // Normal state with data
    return {
      data: roomTypeOptions,
      placeholder,
      disabled: disabled || loading,
      rightSection:
        loading && roomTypes.length > 0 ? (
          <LoadingIndicator size={size} color="gray" />
        ) : (
          selectProps.rightSection
        ),
    };
  };

  const selectState = getSelectState();

  // Render the select component
  return (
    <Select
      {...selectProps}
      size={size}
      value={value ?? null} // Convert undefined to null
      onChange={handleChange} // Use our custom handler
      placeholder={selectState.placeholder}
      clearable={!required}
      searchable
      required={required}
      disabled={selectState.disabled}
      error={selectState.error || error}
      data={selectState.data}
      rightSection={selectState.rightSection}
    />
  );
};
